import { useCallback, useEffect, useState } from "react";

import { ApiFailure, api, type PublicUser } from "../lib/api";
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Skeleton,
  inputClass,
  relativeTime,
} from "../components/ui";

const ROLE_HINT: Record<PublicUser["role"], string> = {
  owner: "Everything, and cannot be removed or demoted by anyone.",
  admin: "Everything except touching the owner or minting another admin.",
  operator: "Approves, rejects and answers chats. No keys, users or settings.",
  viewer: "Read-only.",
};

export function Users({ me }: { me: PublicUser }) {
  const [users, setUsers] = useState<PublicUser[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [temporary, setTemporary] = useState<{ email: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.get<{ items: PublicUser[] }>("/admin/users");
      setUsers(result.items);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "Could not load users.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Only the owner may mint an admin, so that option is hidden rather than
  // offered and then refused by the server.
  const assignable: PublicUser["role"][] =
    me.role === "owner" ? ["admin", "operator", "viewer"] : ["operator", "viewer"];

  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Users</h1>
          <p className="mt-1 text-[13px] text-[var(--muted)]">
            Who can sign in to this console, and what they can do.
          </p>
        </div>
        {!creating && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            Add user
          </Button>
        )}
      </header>

      {error && <ErrorNote>{error}</ErrorNote>}

      {temporary && (
        <Card className="animate-rise border-[var(--brand)]/40 bg-[var(--brand-soft)]">
          <div className="text-[15px] font-semibold text-[var(--brand)]">Temporary password</div>
          <p className="mt-1 text-[13px] text-[var(--muted)]">
            Give this to {temporary.email} over a channel you trust. They must change it on first sign-in,
            and it is not shown again.
          </p>
          <div className="mt-3 flex gap-2">
            <code className="flex-1 rounded-[10px] border border-[var(--border)] bg-[var(--background)] px-3 py-2.5 font-mono text-[13px]">
              {temporary.password}
            </code>
            <Button onClick={() => void navigator.clipboard.writeText(temporary.password)}>Copy</Button>
            <Button variant="ghost" onClick={() => setTemporary(null)}>
              Done
            </Button>
          </div>
        </Card>
      )}

      {creating && (
        <CreateUser
          roles={assignable}
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      )}

      {users === null ? (
        <div className="grid gap-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : (
        <div className="grid gap-3">
          {users.map((u) => (
            <Card key={u.id}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[15px] font-medium">{u.displayName}</span>
                    <Badge tone={u.role === "owner" ? "brand" : "neutral"}>{u.role}</Badge>
                    {!u.isActive && <Badge tone="danger">deactivated</Badge>}
                    {u.twoFactorEnabled && <Badge tone="ok">2FA</Badge>}
                    {u.id === me.id && <Badge tone="neutral">you</Badge>}
                  </div>
                  <div className="mt-1 text-[13px] text-[var(--muted)]">{u.email}</div>
                  <div className="mt-1 text-[12px] text-[var(--faint)]">
                    last signed in {relativeTime(u.lastLoginAt)}
                  </div>
                </div>

                {u.role !== "owner" && u.id !== me.id && (
                  <div className="flex shrink-0 gap-2">
                    <Button
                      onClick={() => {
                        void api
                          .post<{ temporaryPassword: string }>(`/admin/users/${u.id}/reset-password`)
                          .then((r) => setTemporary({ email: u.email, password: r.temporaryPassword }))
                          .catch((err: unknown) =>
                            setError(err instanceof ApiFailure ? err.error.message : "Could not reset."),
                          );
                      }}
                    >
                      Reset password
                    </Button>
                    <Button
                      variant={u.isActive ? "danger" : "secondary"}
                      onClick={() => {
                        const action = u.isActive ? "deactivate" : "activate";
                        if (u.isActive && !confirm(`Deactivate ${u.email}? They will be signed out everywhere.`))
                          return;
                        void api
                          .post(`/admin/users/${u.id}/${action}`)
                          .then(load)
                          .catch((err: unknown) =>
                            setError(err instanceof ApiFailure ? err.error.message : "Could not change."),
                          );
                      }}
                    >
                      {u.isActive ? "Deactivate" : "Activate"}
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateUser({
  roles,
  onCancel,
  onCreated,
}: {
  roles: PublicUser["role"][];
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<PublicUser["role"]>(roles[0] ?? "viewer");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Card className="animate-rise">
      <h2 className="text-[15px] font-semibold">Add a user</h2>
      <div className="mt-4 grid max-w-[440px] gap-4">
        <Field label="Email">
          <input
            className={inputClass}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
        </Field>
        <Field
          label="Temporary password"
          hint="At least 12 characters. They will be forced to change it on first sign-in."
        >
          <input
            className={inputClass}
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="Role" hint={ROLE_HINT[role]}>
          <select
            className={inputClass}
            value={role}
            onChange={(e) => setRole(e.target.value as PublicUser["role"])}
          >
            {roles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>

        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="flex gap-2">
          <Button
            variant="primary"
            disabled={busy || !email.trim() || password.length < 12}
            onClick={() => {
              setBusy(true);
              setError(null);
              void api
                .post("/admin/users", { email, password, role })
                .then(onCreated)
                .catch((err: unknown) =>
                  setError(err instanceof ApiFailure ? err.error.message : "Could not create the user."),
                )
                .finally(() => setBusy(false));
            }}
          >
            {busy ? "Creating…" : "Create user"}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}
